import { ArrowDownTrayIcon, ChevronLeftIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import { PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { QueryObserverResult, useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";

import { useFileUpload } from "@/api/hooks/useFileUpload";
import { useIsBuildingOwner } from "@/api/hooks/useIsBuildingOwner";
import { ApiResponse } from "@/api/rest/core/types";
import { UserInfo } from "@/api/rest/models/account";
import { HouseholdData } from "@/api/rest/models/household";
import { QuestionMessageType } from "@/api/rest/models/question";
import { CaseStatus } from "@/api/rest/models/warranty";
import { downloadDocument2, getDocumentUrl2 } from "@/api/rest/services/documentService";
import { fetchHouseholdData } from "@/api/rest/services/householdService";
import { getQuestion, sendFiles, sendMessage, updateStatus } from "@/api/rest/services/questionService";
import { Banner } from "@/components/common/Banner.tsx";
import { ChatMessage, ChatMessageHolder, ChatMessageType } from "@/components/common/ChatMessage.tsx";
import ChatVideo from "@/components/common/ChatVideo";
import ClosableBanner from "@/components/common/ClosableBanner";
import UploadStatus from "@/components/common/UploadStatus";
import { QuestionStatusDropdown } from "@/components/question-contents/QuestionStatusDropdown";
import UploadFilesPopup from "@/components/UploadFilesPopup.tsx";
import LoadingLogo from "@/components/common/LoadingLogo";
import { isOwnSideMessage } from "@/utils/chatUtils.ts";
import { getAvailableHouseholdAccounts } from "@/utils/householdAccounts";

export default function QuestionPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { questionId } = useParams<{ questionId: string }>();

  const { data: questionData, refetch: refreshQuestion } = useQuery({
    queryKey: ["question", questionId, i18n.language],
    queryFn: async () => {
      if (!questionId) {
        return null;
      }
      const response = await getQuestion(Number(questionId));
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch question data");
      }
      return response.result;
    },
    enabled: Boolean(questionId),
  });

  const { user } = useOutletContext<{
    user: UserInfo;
    currentHousehold: {
      isLoading: boolean;
      data: {
        result: HouseholdData | undefined;
      };
      refetch: () => Promise<QueryObserverResult>;
    };
  }>();

  const currentHousehold = useQuery({
    queryKey: ["household", questionData?.question.householdId, i18n.language],
    queryFn: async () => {
      if (!questionData?.question.householdId) {
        return null;
      }
      const response = await fetchHouseholdData(questionData.question.householdId, i18n.language);
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch household data");
      }
      return response.result;
    },
    enabled: Boolean(questionData?.question.householdId),
  });

  const householdData = currentHousehold.data;
  const loading = currentHousehold.isLoading;

  const availableAccounts = useMemo(() => {
    return getAvailableHouseholdAccounts(user, householdData);
  }, [householdData, user]);

  const amIBuildingOwner = useIsBuildingOwner(user, householdData?.building);

  const sortedMessages = useMemo(() => {
    if (!questionData?.messages) {
      return [];
    }
    return [...questionData.messages].sort((a, b) => {
      const aIsAiHistory = a.type === QuestionMessageType.AI_HISTORY;
      const bIsAiHistory = b.type === QuestionMessageType.AI_HISTORY;

      if (aIsAiHistory && !bIsAiHistory) {
        return -1;
      }
      if (!aIsAiHistory && bIsAiHistory) {
        return 1;
      }

      return a.createdAtTimestamp.diff(b.createdAtTimestamp);
    });
  }, [questionData?.messages]);

  const messageContent = useRef<HTMLTextAreaElement>(null);
  const [isSending, setIsSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const { upload, progress: uploadProgress, error: sendError, setError: setSendError } = useFileUpload();

  const addFiles = useCallback(
    async (files: File[]): Promise<ApiResponse<unknown>> => {
      setPendingFiles([...pendingFiles, ...files]);
      return Promise.resolve({ success: true, result: null });
    },
    [pendingFiles],
  );

  const submitFilesAndMessage = useCallback(async () => {
    if (isSending || !questionId) {
      return;
    }

    const message = messageContent.current?.value ?? "";
    const hasMessage = message.trim().length > 0;
    const hasFiles = pendingFiles.length > 0;

    if (!hasMessage && !hasFiles) {
      return;
    }

    if (!availableAccounts || availableAccounts.length < 1) {
      return;
    }

    try {
      setIsSending(true);

      // Upload first: it is the slow part that can fail, and then nothing is sent and the text stays.
      const uploads = await upload(pendingFiles);
      if (!uploads) {
        return;
      }

      if (hasMessage) {
        const messageResponse = await sendMessage(Number(questionId), {
          message: message,
          accountId: availableAccounts[0].id,
        });

        if (!messageResponse.success) {
          setSendError(messageResponse.message);
          return;
        }
        messageContent.current!.value = "";
      }

      if (uploads.length > 0) {
        const filesResponse = await sendFiles(Number(questionId), availableAccounts[0].id, uploads);

        if (filesResponse.success) {
          setPendingFiles([]);
        } else {
          setSendError(filesResponse.message);
        }
      }

      await refreshQuestion();
    } finally {
      setIsSending(false);
    }
  }, [pendingFiles, questionId, availableAccounts, refreshQuestion, isSending, upload, setSendError]);

  const handleChangeStatus = useCallback(
    async (status: CaseStatus) => {
      if (!questionId || !householdData?.building?.buildingId) {
        return;
      }
      const response = await updateStatus(Number(questionId), {
        buildingId: householdData.building.buildingId,
        status: status,
      });
      if (response.success) {
        await refreshQuestion();
      }
    },
    [questionId, householdData, refreshQuestion],
  );

  if (!user || loading || !questionData || !householdData) {
    return <LoadingLogo fullPage size="lg" />;
  }

  return (
    <main className="flex flex-grow flex-col p-4 md:p-12 md:overflow-y-auto scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-white items-center">
      <div className="flex flex-grow self-stretch flex-col">
        <div className="flex flex-row pb-4">
          <ChevronLeftIcon
            className="h-9 w-9 mr-2 hover:bg-slate-300 focus-visible:bg-dobu-orange cursor-pointer rounded"
            onClick={() => navigate(-1)}
          />
          <h2 className="text-3xl font-bold text-center mr-4">
            {t("question.questions")} - {questionData.question.title}
          </h2>
        </div>
        <div className="flex flex-col ml-12 mt-3 pb-4">
          <h2 className="text-xl">
            <strong>{t("question.status.status")}: </strong>
            {amIBuildingOwner ? (
              <QuestionStatusDropdown questionData={questionData.question} onStatusChange={handleChangeStatus} />
            ) : (
              t(questionData.question.localisedStatus)
            )}
          </h2>
          {amIBuildingOwner && (
            <>
              <h2 className="text-xl">
                <strong>{t("question.details.address")}: </strong>
                {householdData.building.street} - {householdData.household.title}
              </h2>
            </>
          )}
        </div>
        <div className="flex flex-col xl:px-[20%]">
          <ClosableBanner message={t("question.emailNotification")} storageKey="question-email-notification-banner" />
          <ChatMessageHolder>
            {sortedMessages.map((message) => {
              const displayOnRight = isOwnSideMessage(
                message.accountId,
                householdData.building.ownerAccountIds,
                amIBuildingOwner,
              );
              return (
                <ChatMessage
                  key={message.id}
                  type={displayOnRight ? ChatMessageType.QUESTION : ChatMessageType.RESPONSE}
                >
                  <div className="flex flex-col">
                    {message.type !== QuestionMessageType.AI_HISTORY && (
                      <span className={clsx("text-sm text-gray-500", displayOnRight && "self-end")}>
                        {message.userName} - {message.createdAtTimestamp.format("LL")}
                      </span>
                    )}
                    {message.type === QuestionMessageType.AI_HISTORY && (
                      <span className={clsx("text-sm text-gray-500", displayOnRight && "self-end")}>
                        {t("question.previousChatWithBot")}
                      </span>
                    )}
                    {message.type === QuestionMessageType.TEXT && (
                      <span className={clsx(displayOnRight && "self-end", "whitespace-pre-wrap break-words")}>
                        {message.content}
                      </span>
                    )}
                    {message.type === QuestionMessageType.AI_HISTORY && (
                      <span
                        className={clsx(displayOnRight && "self-end", "whitespace-pre-wrap text-gray-500 break-words")}
                      >
                        {message.content}
                      </span>
                    )}
                    {message.type === QuestionMessageType.IMAGE && (
                      <img
                        src={getDocumentUrl2(null, true, message.content)}
                        alt={t("question.messageWithImage")}
                        className="max-w-[300px] max-h-[300px] md:max-w-[40vw] md:max-h-[40vh] object-contain m-0 rounded"
                      />
                    )}
                    {message.type === QuestionMessageType.FILE && (
                      <ChatVideo hash={message.content} mimeType={message.mimeType} />
                    )}
                    {(message.type === QuestionMessageType.FILE || message.type === QuestionMessageType.IMAGE) && (
                      <span
                        className={clsx(
                          "bg-slate-300 hover:bg-slate-400 cursor-pointer p-2 rounded",
                          displayOnRight && "ss",
                        )}
                        onClick={() => downloadDocument2(getDocumentUrl2(null, true, message.content), null, true)}
                      >
                        <ArrowDownTrayIcon className="w-4 h-4 -mt-0.5 inline mx-2" />
                        {t("question.downloadFile")}
                      </span>
                    )}
                  </div>
                </ChatMessage>
              );
            })}
          </ChatMessageHolder>
        </div>
        <div className="flex-grow" />
        <div className="flex flex-col xl:px-[20%]">
          <label className="flex flex-grow relative">
            <span className="mr-2 mb-2 absolute right-0 bottom-0 flex flex-row gap-2">
              <UploadFilesPopup className="flex flex-row" uploadCallback={addFiles}>
                <PaperClipIcon className="w-6 h-6 inline text-gray-300 hover:text-dobu-orange cursor-pointer" />
              </UploadFilesPopup>

              <PaperAirplaneIcon
                className={clsx(
                  "w-6 h-6 inline",
                  isSending
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-300 hover:text-dobu-orange cursor-pointer",
                )}
                onClick={submitFilesAndMessage}
              />
            </span>{" "}
            <div className="flex flex-col w-full gap-2">
              {pendingFiles.length > 0 && (
                <div className="flex flex-wrap border border-gray-300 rounded-md p-2 bg-white gap-2">
                  {pendingFiles.map((file, i) => (
                    <span key={i} className="rounded-md py-2 px-4 bg-gray-200">
                      <PaperClipIcon className="w-4 h-4 inline -mt-0.5 mr-2" /> {file.name}
                    </span>
                  ))}
                </div>
              )}
              <UploadStatus progress={uploadProgress} error={sendError} />
              <textarea
                className="flex-grow border border-gray-300 rounded-md p-2 scrollbar-thin scrollbar-track-white scrollbar-thumb-slate-100 whitespace-pre-wrap"
                placeholder={amIBuildingOwner ? t("question.sendMessageToOwner") : t("question.sendMessageToDeveloper")}
                rows={5}
                ref={messageContent}
              />
            </div>
          </label>
          <div className="mt-2">
            {questionData.question.status === CaseStatus.CLOSED && (
              <Banner message={t("question.closedByService")} success={false} />
            )}
            {questionData.question.status === CaseStatus.SOLVED && (
              <Banner message={t("question.markedAsSolved")} success />
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
